import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BsThreeDotsVertical } from "react-icons/bs";
import { CloseButton, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import AddClient from "../components/AddClient";

const ActiveClients = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState("All");
  const [clients, setClients] = useState([]);
  const [removeClient, setRemoveClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewPlan, setRenewPlan] = useState("1");
  const [renewStartDate, setRenewStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const modalRef = useRef();
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchId, setSearchId] = useState("");
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [itemsPerPage] = useState(10);

  const handleAddClientOpen = () => setIsAddClientOpen(true);
  const handleAddClientClose = () => {
    setIsAddClientOpen(false);
    fetchActiveClients();
  };

  const fetchActiveClients = async (page = 1, planFilter = plan, search = searchTerm, searchClientId = searchId) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString()
      });
      
      if (planFilter && planFilter !== "All") {
        params.append('plan', planFilter.toString());
      }
      
      if (search && search.trim() !== "") {
        params.append('search', search.trim());
      }

      if (searchClientId && searchClientId.trim() !== "") {
        params.append('searchId', searchClientId.trim());
      }

      console.log(params.toString());
      const response = await axios.get(
        `https://klimkaara-backend-pgnq.onrender.com/admins/getactiveclients?${params.toString()}`
      );
      
      setClients(response.data.clients);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalClients(response.data.totalClients);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchActiveClients(1, plan, searchTerm, searchId);
  }, []);

  // Handle page changes
  useEffect(() => {
    fetchActiveClients(currentPage, plan, searchTerm, searchId);
  }, [currentPage]);

  // Handle filter changes
  useEffect(() => {
    setCurrentPage(1);
    fetchActiveClients(1, plan, searchTerm, searchId);
  }, [plan]);

  // Handle search changes with debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      fetchActiveClients(1, plan, searchTerm, searchId);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, searchId]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchId = (e) => {
    setSearchId(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const clearSearchId = () => {
    setSearchId("");
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePlanChange = (e) => {
    const newPlan = e.target.value === "All" ? e.target.value : parseInt(e.target.value);
    setPlan(newPlan);
  };

  const closeModal = () => {
    setRemoveClient(null);
  };

  const closeActionModal = () => {
    setSelectedClient(null);
    setShowActionModal(false);
  };

  const closeRenewModal = () => {
    setShowRenewModal(false);
    setRenewPlan("1");
    setRenewStartDate(new Date().toISOString().slice(0, 10));
  };

  const handleClientAction = (client) => {
    setSelectedClient(client);
    setShowActionModal(true);
  };

  // Handle client row click to navigate to details
  const handleClientRowClick = (client) => {
    navigate(`/clients/${client.clientId}`);
  };

  // Prevent row click when clicking on actions button
  const handleActionClick = (e, client) => {
    e.stopPropagation();
    handleClientAction(client);
  };

  const handleDeleteClick = () => {
    setRemoveClient(selectedClient);
    setShowActionModal(false);
  };

  const handleRenewClick = () => {
    setShowActionModal(false);
    setShowRenewModal(true);
  };

  const confirmRenewal = async () => {
    try {
      setIsLoading(true);
      const requestData = { plan: parseInt(renewPlan) };
      
      // If client is expired, include start date
      if (selectedClient.planExpired) {
        requestData.startDate = renewStartDate;
      }
      
      const response = await axios.post(
        `https://klimkaara-backend-pgnq.onrender.com/clients/renew/${selectedClient._id}`,
        requestData
      );
      if (response.status === 200) {
        toast.success(`Client renewed successfully for ${renewPlan} ${renewPlan === "1" ? "month" : "months"}`);
        setIsLoading(false);
        closeRenewModal();
        setSelectedClient(null);
        // Refresh the current page
        fetchActiveClients(currentPage, plan, searchTerm, searchId);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Failed to renew client");
      console.error(error);
    }
  };

  const confirmRemoval = async () => {
    try {
      setIsLoading(true);
      const response = await axios.delete(
        `https://klimkaara-backend-pgnq.onrender.com/clients/delete/${removeClient._id}`
      );
      if (response.status === 200) {
        toast.success("Client deleted successfully");
        setIsLoading(false);
        closeModal();
        // Refresh the current page
        fetchActiveClients(currentPage, plan, searchTerm, searchId);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  return (
    <div className="container mx-auto p-8 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        {/* ID Search Field */}
        <form className="flex justify-start items-center w-1/3">
          <input
            type="text"
            placeholder="Search by Client ID"
            className="p-2 border-2 border-gray-300 rounded-lg w-full text-black"
            value={searchId}
            onChange={handleSearchId}
          />
          <button
            type="button"
            className="bg-gray-200 p-2 rounded-lg ml-2"
            onClick={clearSearchId}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="black"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </form>

        {/* Name/Phone Search Field */}
        <form className="flex justify-end items-center w-1/3">
          <input
            type="text"
            placeholder="Search for a client by name or phone"
            className="p-2 border-2 border-gray-300 rounded-lg w-full text-black"
            value={searchTerm}
            onChange={handleSearch}
          />
          <button
            type="button"
            className="bg-gray-200 p-2 rounded-lg ml-2"
            onClick={clearSearch}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="black"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </form>
      </div>
      
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold mb-6">Active Clients</h1>
        <button
          className="bg-[#fded22] text-black px-4 py-2 rounded-full font-semibold hover:bg-yellow-300 transition flex items-center"
          onClick={handleAddClientOpen}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Client
        </button>
      </div>

      {/* Pagination Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-gray-400">
          Showing {clients.length} of {totalClients} active clients
          {(searchTerm || searchId || plan !== "All") && " (filtered)"}
        </p>
        <p className="text-gray-400">
          Page {currentPage} of {totalPages}
        </p>
      </div>

      {/* Dropdown Filters */}
      <div className="flex justify-between mb-4">
        <select
          value={plan}
          onChange={handlePlanChange}
          className="md:w-1/4 p-2 rounded-lg bg-gray-800 text-white"
        >
          <option value="All">All Plans</option>
          <option value="1">1 Month</option>
          <option value="3">3 Month</option>
          <option value="6">6 Month</option>
          <option value="12">1 Year</option>
        </select>
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center">
          <svg
            className="animate-spin h-5 w-5 text-white mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      ) : clients.length === 0 ? (
        <p className="text-center text-gray-400">No Active Clients Found</p>
      ) : (
        <table className="min-w-full bg-gray-800 text-white rounded-lg shadow-lg">
          <thead>
            <tr className="text-left border-b-2 border-gray-600">
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Client Id</th>
              <th className="py-2 px-4">Phone Number</th>
              <th className="py-2 px-4">Category</th>
              <th className="py-2 px-4">Joined Date</th>
              <th className="py-2 px-4">Type of Plan</th>
              <th className="py-2 px-4">Plan Expired</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client, index) => (
              <tr 
                key={index} 
                className="border-b border-gray-700 hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                onClick={() => handleClientRowClick(client)}
              >
                <td className="py-2 px-4">{client.name}</td>
                <td className="py-2 px-4">{parseInt(client.clientId.split("-")[1]).toString()}</td>
                <td className="py-2 px-4">{client?.phonenumber}</td>
                <td className="py-2 px-4">{client.category}</td>
                <td className="py-2 px-4">
                  {new Date(client.start).toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })}
                </td>
                <td className="py-2 px-4">
                  {client.plan} {client.plan === 1 ? "Month" : "Months"}
                </td>
                <td className="py-2 px-4">
                  <span
                    className={`px-3 py-1 rounded-full text-white font-semibold ${
                      client.planExpired ? "bg-red-600" : "bg-green-600"
                    }`}
                  >
                    {client.planExpired ? "Yes" : "No"}
                  </span>
                </td>
                <td className="py-2 px-4">
                  <div className="relative inline-block text-left">
                    <button
                      onClick={(e) => handleActionClick(e, client)}
                      className="text-gray-400 hover:text-white"
                    >
                      <BsThreeDotsVertical />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-lg ${
              currentPage === 1
                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                : "bg-gray-700 text-white hover:bg-gray-600"
            }`}
          >
            Previous
          </button>

          {/* Page Numbers */}
          {[...Array(totalPages)].map((_, index) => {
            const page = index + 1;
            if (
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 2 && page <= currentPage + 2)
            ) {
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 rounded-lg ${
                    currentPage === page
                      ? "bg-[#fded22] text-black"
                      : "bg-gray-700 text-white hover:bg-gray-600"
                  }`}
                >
                  {page}
                </button>
              );
            } else if (page === currentPage - 3 || page === currentPage + 3) {
              return (
                <span key={page} className="px-2 text-gray-400">
                  ...
                </span>
              );
            }
            return null;
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-lg ${
              currentPage === totalPages
                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                : "bg-gray-700 text-white hover:bg-gray-600"
            }`}
          >
            Next
          </button>
        </div>
      )}

      {/* Renew Modal */}
      {showRenewModal && selectedClient && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-gray-900 p-8 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-bold mb-4 text-white">Renew Client</h2>
            <p className="text-gray-300 mb-4">
              {selectedClient.planExpired 
                ? `${selectedClient.name}'s plan has expired. Set new start date and plan:` 
                : `Select renewal plan for ${selectedClient.name}:`
              }
            </p>
            
            <div className="space-y-4 mb-6">
              {selectedClient.planExpired && (
                <div>
                  <label className="block">
                    <span className="text-yellow-400 font-medium mb-2 block">New Start Date</span>
                    <input
                      type="date"
                      value={renewStartDate}
                      onChange={(e) => setRenewStartDate(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                    />
                  </label>
                </div>
              )}
              
              <div>
                <label className="block">
                  <span className="text-yellow-400 font-medium mb-2 block">Renewal Plan</span>
                  <select
                    value={renewPlan}
                    onChange={(e) => setRenewPlan(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-black"
                  >
                    <option value="1">1 Month</option>
                    <option value="3">3 Months</option>
                    <option value="6">6 Months</option>
                    <option value="12">1 Year</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={confirmRenewal}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Renewing...
                  </div>
                ) : (
                  "Confirm Renewal"
                )}
              </button>
              <button
                onClick={closeRenewModal}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedClient && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-gray-900 p-8 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-bold mb-4 text-white">Client Actions</h2>
            <p className="text-gray-300 mb-6">What would you like to do with {selectedClient.name}?</p>
            <div className="flex flex-col space-y-3">
              <button
                onClick={handleRenewClick}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Renewing...
                  </div>
                ) : (
                  "🔄 Renew Client"
                )}
              </button>
              <button
                onClick={handleDeleteClick}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                disabled={isLoading}
              >
                🗑️ Delete Client
              </button>
              <button
                onClick={closeActionModal}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {removeClient && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div
            ref={modalRef}
            className="bg-gray-900 p-8 rounded-lg shadow-lg w-96"
          >
            <h2 className="text-lg font-bold mb-4">Confirm Removal</h2>
            <p>Are you sure you want to remove {removeClient.name}?</p>
            <div className="flex justify-end mt-4">
              <button
                onClick={closeModal}
                className="mr-4 bg-red-600 text-white px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoval}
                className="bg-green-600 text-white px-4 py-2 rounded-lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  "Yes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {isAddClientOpen && <AddClient onClose={handleAddClientClose} />}
    </div>
  );
};

export default ActiveClients; 